import React from 'react';
import styled from 'styled-components';
import { CardActionArea, Tooltip, Typography } from '@material-ui/core';

import { authConfig } from '../Auth.config';
import { AuthMethod, AuthMethodId } from '../Auth.interface';

const StyledDiv = styled.div`
  width: 100%;
  .auth-method-list {
    display: flex;
    gap: 2px;
  }
  .auth-method-item {
    width: auto;
    overflow: hidden;
    border-radius: 4px;

    /* auth method specific styling */
    &[data-auth-method='${AuthMethodId.Google}'] {
      .auth-method-img {
        transform: scale(0.88);
      }
    }
    &[data-auth-method='${AuthMethodId.Okta}'] {
      .auth-method-img {
        transform: scale(0.88);
      }
    }

    &.selected {
      .auth-method-indicator {
        opacity: 1;
      }
    }

    .auth-method-area {
      padding: 4px;
      /* disable google rendered button inside iframe */
      &.disabled {
        pointer-events: none;
      }
      .auth-method-img {
        display: block;
        height: 38px;
      }
    }

    .auth-method-indicator {
      height: 4px;
      width: 50%;
      opacity: 0;
      margin: 0 auto;
      border-radius: 2px;
      background-color: ${props => props.theme.palette.primary.main};
      transition: opacity 0.5s cubic-bezier(0.075, 0.82, 0.165, 1);
    }
  }
`;

export interface AuthMethodListPickerProps {
  value?: AuthMethodId;
  options?: AuthMethod[];
  label?: React.ReactNode;
  disabled?: boolean;
  disableAuthMethods?: AuthMethodId[];
  className?: string;
  style?: React.CSSProperties;
  onChange?: (authMethod: AuthMethod | undefined) => void;
}

const AuthMethodListPicker: React.FC<AuthMethodListPickerProps> = props => {
  const {
    value,
    options,
    label,
    disabled,
    disableAuthMethods,
    className,
    style,
    onChange
  } = props;
  const methods = options || authConfig.methods;
  const classes = ['auth-method-list-picker'];
  className && classes.push(className);
  return (
    <StyledDiv className={classes.join(' ')} style={style}>
      {label && (
        <div className='auth-method-list-label'>
          {typeof label === 'string' ? (
            <Typography variant='caption'>{label}</Typography>
          ) : (
            label
          )}
        </div>
      )}
      <div className='auth-method-list'>
        {methods.map(authMethod => {
          const selected = value === authMethod.id;
          const areaClasses = ['auth-method-area'];
          const isDisabled =
            disabled || disableAuthMethods?.includes(authMethod.id);
          isDisabled && areaClasses.push('disabled');
          const areaClassName = areaClasses.join(' ');
          return (
            <div
              key={authMethod.id}
              data-auth-method={authMethod.id}
              className={'auth-method-item ' + (selected ? 'selected' : '')}
            >
              <Tooltip arrow title={authMethod.name}>
                <span>
                  {/* NOTE: google auth method is rendered separately */}
                  {authMethod.id === AuthMethodId.Google ? (
                    <div
                      className={areaClassName}
                      ref={element => {
                        // get the google login button iframe
                        const iframe =
                          element?.querySelector<HTMLIFrameElement>('iframe');
                        if (!iframe) {
                          // do nothing and skip if no iframe
                        } else if (isDisabled) {
                          // prevent focusing google login button iframe.
                          // note that this much is sufficient since
                          // disabling is not strict and is mainly for ux purposes
                          iframe.tabIndex = -1;
                          iframe.blur();
                        } else {
                          // when enabled, reset tabIndex
                          iframe.removeAttribute('tabindex');
                        }
                      }}
                    />
                  ) : (
                    <CardActionArea
                      disabled={isDisabled}
                      className={areaClassName}
                      onClick={() => {
                        onChange?.(selected ? undefined : authMethod);
                      }}
                    >
                      <img
                        draggable='false'
                        className='auth-method-img'
                        src={authMethod.image}
                        alt={`${authMethod.name} logo`}
                      />
                    </CardActionArea>
                  )}
                </span>
              </Tooltip>
              <div className='auth-method-indicator' />
            </div>
          );
        })}
      </div>
    </StyledDiv>
  );
};

export default AuthMethodListPicker;
